import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Chip,
  Tooltip
} from '@mui/material';
import {
  X,
  Library,
  Plus,
  Edit2,
  Trash2,
  Check,
  RotateCcw,
  Music2,
  PlayCircle
} from 'lucide-react';
import {
  MAX_PLAYLIST_NAME_LENGTH,
  sanitizePlaylistName,
  isValidPlaylistChar
} from '../../utils/playlistUtils';

export default function PlaylistManagerDialog({
  open,
  onClose,
  myPlaylists = [],
  systemPlaylists = [],
  currentPlaylistId,
  onSelectPlaylist,
  onCreatePlaylist,
  onRenamePlaylist,
  onDeletePlaylist
}) {
  const [newListName, setNewListName] = useState('');
  const [createError, setCreateError] = useState('');
  
  // Estado para renombrado in-place
  const [editingListId, setEditingListId] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [editError, setEditError] = useState('');

  // Estado para confirmación de eliminación
  const [deletingList, setDeletingList] = useState(null);

  const existingNames = [
    ...myPlaylists.map(p => p.name.toLowerCase()),
    ...systemPlaylists.map(p => p.name.toLowerCase())
  ];

  const handleCreateKeyDown = (e) => {
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreateSubmit();
      }
      return;
    }
    if (e.ctrlKey || e.metaKey) return;
    if (e.target.value.length >= MAX_PLAYLIST_NAME_LENGTH) {
      e.preventDefault();
      return;
    }
    if (!isValidPlaylistChar(e.key)) {
      e.preventDefault();
    }
  };

  const handleCreateChange = (e) => {
    const clean = sanitizePlaylistName(e.target.value);
    setNewListName(clean);

    if (!clean.trim()) {
      setCreateError('');
    } else if (existingNames.includes(clean.trim().toLowerCase())) {
      setCreateError('Ya existe una lista con este nombre');
    } else {
      setCreateError('');
    }
  };

  const handleCreateSubmit = () => {
    const clean = newListName.trim();
    if (!clean) {
      setCreateError('Ingresa un nombre para la lista');
      return;
    }
    if (existingNames.includes(clean.toLowerCase())) {
      setCreateError('Ya existe una lista con este nombre');
      return;
    }
    onCreatePlaylist(clean);
    setNewListName('');
    setCreateError('');
  };

  const startRename = (list) => {
    setEditingListId(list.id || list.name);
    setEditNameValue(list.name);
    setEditError('');
  };

  const cancelRename = () => {
    setEditingListId(null);
    setEditNameValue('');
    setEditError('');
  };

  const handleEditKeyDown = (e, list) => {
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)) {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveRename(list);
      } else if (e.key === 'Escape') {
        cancelRename();
      }
      return;
    }
    if (e.ctrlKey || e.metaKey) return;
    if (e.target.value.length >= MAX_PLAYLIST_NAME_LENGTH) {
      e.preventDefault();
      return;
    }
    if (!isValidPlaylistChar(e.key)) {
      e.preventDefault();
    }
  };

  const handleEditChange = (e, currentName) => {
    const clean = sanitizePlaylistName(e.target.value);
    setEditNameValue(clean);

    if (!clean.trim()) {
      setEditError('El nombre no puede estar vacío');
    } else if (
      clean.trim().toLowerCase() !== currentName.toLowerCase() &&
      existingNames.includes(clean.trim().toLowerCase())
    ) {
      setEditError('Ya existe una lista con este nombre');
    } else {
      setEditError('');
    }
  };

  const saveRename = (list) => {
    const clean = editNameValue.trim();
    if (!clean) {
      setEditError('El nombre no puede estar vacío');
      return;
    }
    if (
      clean.toLowerCase() !== list.name.toLowerCase() &&
      existingNames.includes(clean.toLowerCase())
    ) {
      setEditError('Ya existe una lista con este nombre');
      return;
    }
    onRenamePlaylist(list.id || list.name, clean);
    cancelRename();
  };

  const confirmDelete = () => {
    if (deletingList) {
      onDeletePlaylist(deletingList.id || deletingList.name);
      setDeletingList(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#0f081d !important',
            backgroundColor: '#0f081d !important',
            backdropFilter: 'none !important',
            border: '1px solid rgba(168, 85, 247, 0.35)',
            borderRadius: { xs: 2.5, sm: 3 },
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 50px rgba(168, 85, 247, 0.25)',
            color: '#fff',
            overflow: 'hidden',
            width: { xs: 'calc(100% - 20px)', sm: '92%', md: '700px' },
            maxWidth: '700px',
            m: { xs: '10px auto', sm: '24px auto' },
            maxHeight: { xs: '92vh', sm: '85vh' },
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Encabezado */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            pb: { xs: 1.5, sm: 2 },
            pt: { xs: 1.8, sm: 2.2 },
            px: { xs: 2, sm: 3 },
            mb: 0,
            background: '#150a26'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.2, sm: 1.5 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25), rgba(0, 240, 255, 0.25))',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                flexShrink: 0
              }}
            >
              <Library size={18} color="#c084fc" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.02rem', sm: '1.2rem' }, lineHeight: 1.25 }}>
                Gestión de Playlists
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(192, 132, 252, 0.85)', fontWeight: 500, fontSize: { xs: '0.72rem', sm: '0.78rem' }, display: 'block', mt: 0.2 }}>
                Crea, renombra o cambia tus listas de canciones
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar modal"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              p: { xs: '6px', sm: '8px' },
              '&:hover': { color: '#ff0055', transform: 'rotate(90deg)', transition: 'all 0.25s ease' }
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        {/* Contenido con scrollbar estilizado */}
        <DialogContent
          sx={{
            py: { xs: 2, sm: 2.8 },
            px: { xs: 1.8, sm: 3 },
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(168, 85, 247, 0.35)',
              borderRadius: '10px',
              '&:hover': { background: 'rgba(168, 85, 247, 0.6)' }
            }
          }}
        >
          {/* Formulario de creación de nueva lista */}
          <Box
            sx={{
              p: { xs: 1.6, sm: 2.2 },
              mt: { xs: 0.5, sm: 1 },
              mb: { xs: 2.2, sm: 3 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.06) 0%, rgba(168, 85, 247, 0.08) 100%)',
              border: '1px solid rgba(0, 240, 255, 0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00f0ff', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
              Crear Nueva Lista Propia
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
              <TextField
                size="small"
                fullWidth
                value={newListName}
                onChange={handleCreateChange}
                onKeyDown={handleCreateKeyDown}
                error={Boolean(createError)}
                helperText={
                  createError || (
                    <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{`${newListName.length}/${MAX_PLAYLIST_NAME_LENGTH}`}</span>
                      <span style={{ opacity: 0.75 }}>(solo letras, números y guiones)</span>
                    </Box>
                  )
                }
                placeholder="Ej: Rock 80s, Cachengue, Cumple..."
                inputProps={{ maxLength: MAX_PLAYLIST_NAME_LENGTH }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.86rem',
                    '& fieldset': { borderColor: 'rgba(0, 240, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#00f0ff' },
                    '&.Mui-focused fieldset': { borderColor: '#00f0ff' }
                  },
                  '& .MuiFormHelperText-root': {
                    color: createError ? '#ff4d6d' : 'rgba(255, 255, 255, 0.5)',
                    fontSize: '0.72rem',
                    mx: 0.5
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleCreateSubmit}
                disabled={Boolean(createError) || !newListName.trim()}
                startIcon={<Plus size={16} />}
                sx={{
                  background: 'linear-gradient(135deg, #00f0ff 0%, #7000ff 100%)',
                  color: '#080a14',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.84rem',
                  flexShrink: 0,
                  py: { xs: 1.1, sm: 1 },
                  px: 2.2,
                  width: { xs: '100%', sm: 'auto' },
                  boxShadow: '0 0 15px rgba(0, 240, 255, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #33f3ff 0%, #851aff 100%)',
                    boxShadow: '0 0 20px rgba(0, 240, 255, 0.6)'
                  },
                  '&.Mui-disabled': { opacity: 0.45, color: 'rgba(255, 255, 255, 0.5)' }
                }}
              >
                Crear Lista
              </Button>
            </Box>
          </Box>

          {/* Listado de Mis Listas Propias */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'rgba(255, 255, 255, 0.9)', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Mis Listas Propias ({myPlaylists.length})</span>
            </Typography>

            {myPlaylists.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)', borderRadius: 2, border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  No tienes listas propias creadas. ¡Crea una nueva arriba!
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {myPlaylists.map((list) => {
                  const isCurrent = (list.id === currentPlaylistId || list.name === currentPlaylistId);
                  const isEditing = editingListId === (list.id || list.name);
                  const songCount = Array.isArray(list.items) ? list.items.length : 0;

                  return (
                    <Box
                      key={list.id || list.name}
                      sx={{
                        p: { xs: 1.5, sm: 1.8 },
                        borderRadius: 2,
                        background: isCurrent ? 'rgba(0, 240, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid',
                        borderColor: isCurrent ? 'rgba(0, 240, 255, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1.2, sm: 2 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: isCurrent ? 'rgba(0, 240, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                          borderColor: isCurrent ? '#00f0ff' : 'rgba(255, 255, 255, 0.18)'
                        }
                      }}
                    >
                      {/* Lado izquierdo: Nombre o Input de Edición */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={editNameValue}
                              onChange={(e) => handleEditChange(e, list.name)}
                              onKeyDown={(e) => handleEditKeyDown(e, list)}
                              error={Boolean(editError)}
                              helperText={editError || `${editNameValue.length}/${MAX_PLAYLIST_NAME_LENGTH}`}
                              inputProps={{ maxLength: MAX_PLAYLIST_NAME_LENGTH }}
                              autoFocus
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  background: 'rgba(255, 255, 255, 0.08)',
                                  borderRadius: '8px',
                                  color: '#fff',
                                  fontSize: '0.84rem'
                                },
                                '& .MuiFormHelperText-root': {
                                  color: editError ? '#ff4d6d' : 'rgba(255, 255, 255, 0.5)',
                                  fontSize: '0.7rem'
                                }
                              }}
                            />
                            <Tooltip title="Guardar nombre">
                              <IconButton
                                size="small"
                                onClick={() => saveRename(list)}
                                sx={{ color: '#00f078', bgcolor: 'rgba(0, 240, 120, 0.15)', p: '8px', '&:hover': { bgcolor: 'rgba(0, 240, 120, 0.3)' } }}
                              >
                                <Check size={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton
                                size="small"
                                onClick={cancelRename}
                                sx={{ color: 'rgba(255, 255, 255, 0.6)', bgcolor: 'rgba(255, 255, 255, 0.08)', p: '8px', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                              >
                                <RotateCcw size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  color: '#ffffff',
                                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                  wordBreak: 'break-word'
                                }}
                              >
                                {list.name}
                              </Typography>
                              {isCurrent && (
                                <Chip
                                  label="Activa en panel"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    fontWeight: 800,
                                    background: 'rgba(0, 240, 255, 0.2)',
                                    color: '#00f0ff',
                                    border: '1px solid rgba(0, 240, 255, 0.4)'
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Music2 size={12} /> {songCount} {songCount === 1 ? 'canción' : 'canciones'}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Lado derecho: Acciones */}
                      {!isEditing && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'space-between', sm: 'flex-end' },
                            gap: 1,
                            flexShrink: 0,
                            borderTop: { xs: '1px solid rgba(255, 255, 255, 0.06)', sm: 'none' },
                            pt: { xs: 1, sm: 0 },
                            mt: { xs: 0.5, sm: 0 }
                          }}
                        >
                          {!isCurrent ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                onSelectPlaylist(list.id || list.name);
                              }}
                              startIcon={<PlayCircle size={14} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                borderColor: 'rgba(0, 240, 255, 0.4)',
                                color: '#00f0ff',
                                py: { xs: 0.6, sm: 0.4 },
                                px: 1.4,
                                flex: { xs: 1, sm: 'none' },
                                '&:hover': {
                                  borderColor: '#00f0ff',
                                  background: 'rgba(0, 240, 255, 0.12)'
                                }
                              }}
                            >
                              Activar
                            </Button>
                          ) : (
                            <Chip
                              label="Cargada"
                              size="small"
                              sx={{
                                height: 26,
                                bgcolor: 'rgba(0, 255, 136, 0.15)',
                                color: '#00ff88',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                border: '1px solid rgba(0, 255, 136, 0.3)',
                                flex: { xs: 1, sm: 'none' }
                              }}
                            />
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Tooltip title="Renombrar lista">
                              <IconButton
                                size="small"
                                onClick={() => startRename(list)}
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                                  p: '7px',
                                  '&:hover': { color: '#00f0ff', bgcolor: 'rgba(0, 240, 255, 0.12)' }
                                }}
                              >
                                <Edit2 size={15} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar lista propia">
                              <IconButton
                                size="small"
                                onClick={() => setDeletingList(list)}
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                                  p: '7px',
                                  '&:hover': { color: '#ff4d6d', bgcolor: 'rgba(255, 77, 109, 0.12)' }
                                }}
                              >
                                <Trash2 size={15} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            py: { xs: 1.2, sm: 1.6 },
            px: { xs: 2, sm: 3 },
            background: 'rgba(10, 13, 22, 0.6)'
          }}
        >
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              textTransform: 'none',
              fontWeight: 600,
              py: { xs: 0.8, sm: 0.6 },
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.08)' }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmación de Eliminación */}
      <Dialog
        open={Boolean(deletingList)}
        onClose={() => setDeletingList(null)}
        PaperProps={{
          sx: {
            background: 'rgba(18, 12, 28, 0.98)',
            border: '1px solid rgba(255, 77, 109, 0.4)',
            borderRadius: 3,
            p: 1,
            width: { xs: 'calc(100% - 24px)', sm: '440px' },
            m: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ color: '#ff4d6d', fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' }, pb: 1 }}>
          Confirmar Eliminación de Lista
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.6, fontSize: '0.86rem' }}>
            ¿Estás seguro de que deseas eliminar la lista <strong>"{deletingList?.name}"</strong>?
            <br />
            Esta acción no se puede deshacer y se removerán las {Array.isArray(deletingList?.items) ? deletingList.items.length : 0} canciones que contiene.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 1.5, px: 2, pt: 1 }}>
          <Button onClick={() => setDeletingList(null)} sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#ff4d6d',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#ff2d55' }
            }}
          >
            Eliminar Lista
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
